import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import "../../NonRKPayInvoiceDetail.scss";
import { ContactService, PaymentService, InvoiceService } from "@services";
import { isEqual } from "@utils/lodash";
import { useGetDisputeHistory } from "@queries/NonRKPayInvoiceQueries";
import { NonRKPayInvoiceStatusHistoryModal, NonRKPayInvoiceDisputeHistoryModal } from "../../../components";
import SendRemindersModal from "../../../../NonRKPayInvoiceList/components/SendRemindersModal";
import PaymentConfirmation from "../../../../PaymentSummary/modals/PaymentConfirmation/PaymentConfirmation";
import EditRecipient from "../../../../PaymentSummary/modals/EditRecipient";
import AddClientContactModal from "../../../../PaymentSummary/modals/AddClientContactModal";
import NonRKPayStickyToolbar from "./components/NonRKPayStickyToolbar/NonRKPayStickyToolbar";
import NonRKPayToolbarActions from "./components/NonRKPayToolbarActions";
import NonRKPayToolbarActionsDropdown from "./components/NonRKPayToolbarActionsDropdown";
import SendNonRKPayInvoiceConfirmationModal from "../SendNonRKPayInvoiceConfirmationModal";
import NonRKPayToolbarMainActions from "./components/NonRKPayToolbarMainActions";
import NonRKPayStickyBanner from "./components/NonRKPayStickyBanner";

const NonRKPayToolbar = ({ nonRKPayInvoiceDetailHook, router, isSuperAdmin, user, clientV4InvoicingDisputes }) => {
	const { lang } = global;
	const {
		selectedClient,
		getInvoiceDetails,
		handleInvoiceData,
		buildInvoiceObject,
		originalInvoice,
		validateInvoice,
		isTimeCardNotesModalEnabled,
		invoiceState: {
			isEstimate,
			invoiceId,
			invoiceVersion,
			totals,
			invoiceNumber,
			billTo,
		}
	} = nonRKPayInvoiceDetailHook;

	const [showInvoiceStatusHistoryModal, setShowInvoiceStatusHistoryModal] = useState(false);
	const [contactData, setContactData] = useState({ isLoading: true });
	const [recipientToUpdate, setRecipientToUpdate] = useState(undefined);
	const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
	const [showSendRecipientsModal, setShowSendRecipientsModal] = useState(false);
	const [showAddClientModal, setShowAddClientModal] = useState(false);
	const [showSendRemindersModal, setShowSendRemindersModal] = useState(false);
	const [estimateValidToSend, setEstimateValidToSend] = useState(false);
	const [sendingInvoice, setSendingInvoice] = useState(false);
	const [unSavedChanges, setUnSavedChanges] = useState(false);
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
	const [showDisputeHistoryModal, setShowDisputeHistoryModal] = useState(false);
	const { data: disputeHistory } = useGetDisputeHistory(invoiceId, {
		enabled: clientV4InvoicingDisputes && !!invoiceId,
		onError: () => {
			utils.notify({
				type: "error",
				message: lang.t("Unable to load invoice dispute history"),
			});
		}
	});
	const [invoiceSent, setInvoiceSent] = useState(false);

	const toggleInvoiceStatusHistoryModal = () =>
		setShowInvoiceStatusHistoryModal(!showInvoiceStatusHistoryModal);

	const toggleDisputeHistoryModal = () =>
		setShowDisputeHistoryModal(!showDisputeHistoryModal);

	const getContacts = async clientId => {
		try {
			const { data } = await ContactService.getAll({ clientId });
			return data;
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error loading client contacts"),
				timeOut: 1500,
			});
			return null;
		} finally {
			setContactData({ isLoading: false });
		}
	};

	const onSendToClientCallback = async () => {
		const invoiceData = await getInvoiceDetails(invoiceId);
		if (invoiceData) handleInvoiceData(invoiceData);
		setInvoiceSent(true);
	};

	const getContactsData = async clientId => {
		setContactData({ isLoading: true });
		if (clientId) {
			const contactsData = await getContacts(clientId);
			if (contactData) {
				setContactData({ isLoading: false, data: contactsData });
			}
		}
	};

	const mapSendInvoiceRecipients = () => ({
		BillingContactEmail: recipientToUpdate.BillingContactEmail,
		InvoiceId: invoiceId,
		SendEmailToMyself: recipientToUpdate.SendEmailToMyself,
		CC: recipientToUpdate.InvoiceCCRecipients,
		BCC: recipientToUpdate.InvoiceBCCRecipients,
	});

	const updateAndSendNonRKPayInvoice = async () => {
		if (validateInvoice()) {
			setSendingInvoice(true);
			const invoiceObject = buildInvoiceObject();
			const model = mapSendInvoiceRecipients();
			try {
				await InvoiceService.updateNonRKPayInvoice(invoiceObject);
				await PaymentService.sendV4Invoices([model]);

				onSendToClientCallback();
				utils.notify({
					type: "success",
					message: lang.t(isEstimate ? "Estimate sent" : "Invoice sent"),
					timeOut: 2000,
				});
			} catch (error) {
				utils.notify({
					type: "error",
					message: lang.t("Error sending invoice"),
					timeOut: 2000,
				});
			} finally {
				setSendingInvoice(false);
			}
		}
	};

	const sendNonRKPayInvoice = async () => {
		try {
			setSendingInvoice(true);
			const model = mapSendInvoiceRecipients();
			const sent = await PaymentService.sendV4Invoices([model]);

			utils.notify({
				type: sent ? "success" : "error",
				message: sent
					? lang.t(isEstimate ? "Estimate sent" : "Invoice sent")
					: lang.t(isEstimate ? "Error sending estimate" : "Error sending invoice"),
				timeOut: 2000,
			});

			onSendToClientCallback();
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t(isEstimate ? "Error sending estimate" : "Error sending invoice"),
				timeOut: 2000,
			});
		} finally {
			setSendingInvoice(false);
		}
	};

	const validateInvoiceToSend = async id => {
		try {
			const validate = await PaymentService.validateNonRKPayInvoicesToSend(id.toString());
			if (validate) {
				if (unSavedChanges) {
					setShowUnsavedChangesModal(true);
					return;
				}
				if (isEstimate || invoiceVersion === 2) {
					setEstimateValidToSend(true);
				} else {
					setShowPaymentConfirmationModal(true);
				}
			}
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error validating invoice"),
				timeOut: 1500,
			});
		}
	};

	const handleSend = async () => {
		const {
			Organization: { NonRKPayPaymentsEnabled },
		} = user;
		const currentInvoice = buildInvoiceObject(true);

		if (
			NonRKPayPaymentsEnabled &&
			Object.values(billTo).filter(val => val === "").length > 0
		) {
			utils.notify({
				type: "error",
				message: "Please fill out all fields in the 'Bill To' section",
				timeOut: 4000,
			});
			return;
		}
		if (!isEqual(originalInvoice, currentInvoice)) {
			setUnSavedChanges(true);
		}
		setShowSendRecipientsModal(true);
	};

	useEffect(() => {
		if (estimateValidToSend && recipientToUpdate) {
			sendNonRKPayInvoice();
			setEstimateValidToSend(false);
		}
	}, [recipientToUpdate, estimateValidToSend]);

	return (
		<>
			<NonRKPayStickyToolbar
				total={utils.currencyFormatter(totals.totalDue)}
				onGoBack={() => router.goBack()}
				nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
			>
				<NonRKPayToolbarActions
					isSuperAdmin={isSuperAdmin}
					nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
					toggleInvoiceStatusHistoryModal={toggleInvoiceStatusHistoryModal}
					toggleDisputeHistoryModal={toggleDisputeHistoryModal}
					isTimeCardNotesModalEnabled={isTimeCardNotesModalEnabled}
					clientV4InvoicingDisputes={clientV4InvoicingDisputes}
					areThereDisputesInHistory={disputeHistory?.length > 0}
				/>
				<NonRKPayToolbarActionsDropdown
					nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
					isSuperAdmin={isSuperAdmin}
					handleSend={handleSend}
					sendingInvoice={sendingInvoice}
				/>
				<NonRKPayToolbarMainActions
					nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
					isSuperAdmin={isSuperAdmin}
					setShowSendRemindersModal={setShowSendRemindersModal}
					handleSend={handleSend}
					sendingInvoice={sendingInvoice}
				/>
			</NonRKPayStickyToolbar>
			<NonRKPayStickyBanner
				nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
				router={router}
				invoiceSent={invoiceSent}
				setInvoiceSent={setInvoiceSent}
			/>
			<NonRKPayInvoiceStatusHistoryModal
				show={showInvoiceStatusHistoryModal}
				invoiceId={invoiceId}
				onClose={toggleInvoiceStatusHistoryModal}
				user={user}
			/>
			<NonRKPayInvoiceDisputeHistoryModal
				show={showDisputeHistoryModal}
				disputeHistory={disputeHistory}
				onClose={toggleDisputeHistoryModal}
				userTimeZoneId={user.TimeZoneId}
			/>
			<SendRemindersModal
				show={showSendRemindersModal}
				onCancel={() => setShowSendRemindersModal(false)}
				contactData={contactData?.data}
				user={user}
				getContacts={() => getContactsData(selectedClient?.value)}
				clientId={selectedClient?.value}
				invoiceId={invoiceId}
				invoiceNumber={invoiceNumber}
				billToEmail={billTo.email}
				billToName={billTo.contact}
			/>
			<PaymentConfirmation
				show={showPaymentConfirmationModal}
				onHide={() => setShowPaymentConfirmationModal(false)}
				onCancel={() => setShowPaymentConfirmationModal(false)}
				invoiceItems={recipientToUpdate ? [recipientToUpdate] : []}
				onSendCallback={onSendToClientCallback}
				invoiceId={invoiceId}
			/>
			<EditRecipient
				show={showSendRecipientsModal}
				onCancel={() => setShowSendRecipientsModal(false)}
				clientId={selectedClient?.value}
				onShowCreateNewClient={() => setShowAddClientModal(true)}
				onCancelCreateNewClient={() => setShowAddClientModal(false)}
				contactData={contactData?.data}
				updateRecipients={setRecipientToUpdate}
				onShowPaymentConfirmationModal={() => validateInvoiceToSend(invoiceId)}
				isEstimate={isEstimate}
				billToEmail={billTo.email}
			/>
			<SendNonRKPayInvoiceConfirmationModal
				show={showUnsavedChangesModal}
				onCancel={() => setShowUnsavedChangesModal(false)}
				isEstimate={isEstimate}
				onSend={() => {
					setShowUnsavedChangesModal(false);
					updateAndSendNonRKPayInvoice();
				}}
			/>
			{showAddClientModal && (
				<AddClientContactModal
					show={showAddClientModal}
					user={user}
					clientId={selectedClient?.value}
					organizationId={user.OrganizationId}
					onCancel={() => setShowAddClientModal(false)}
					onSave={() => {
						setShowAddClientModal(false);
						getContactsData(selectedClient?.value);
					}}
				/>
			)}
		</>
	);
};

NonRKPayToolbar.propTypes = {
	router: PropTypes.object,
	nonRKPayInvoiceDetailHook: PropTypes.object.isRequired,
	isSuperAdmin: PropTypes.bool,
	user: PropTypes.object,
	clientV4InvoicingDisputes: PropTypes.bool,
};

NonRKPayToolbar.defaultProps = {
	router: {},
	isSuperAdmin: false,
	user: {},
	clientV4InvoicingDisputes: false,
};

export default NonRKPayToolbar;
